import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card } from '../../../components/ui';
import { dateFilter } from '../../../components/common/ListPage/listFilters';
import { Cell, ListPage } from '../../../components/common/ListPage/ListPage';
import { StatusBadge } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { labelOf } from '../../../utils/labels';
import { PAY_TYPES } from '../../../constants/payments';
import { PAYMENT_STATUS } from '../../../constants/status';
import { formatDate, toISODate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { teamService } from '../../team/services/teamService';
import { payrollService } from '../services/payrollService';
import type { ReactNode } from 'react';

const TABS = [{ value: '', label: 'All' }, ...Object.entries(PAYMENT_STATUS).map(([value, [label]]) => ({ value, label }))];

export const periodText = (s) => `${formatDate(s.periodFrom)} – ${formatDate(s.periodTo)}`;

const staffFilter = {
  key: 'userId',
  label: 'Staff',
  icon: 'user',
  fetcher: (search: string) => teamService.search(search).then((list) => list.filter((u) => u.role !== 'owner')),
  getDescription: (u) => labelOf(PAY_TYPES, u.payType),
};

function Stat({ label, value, note, tone }: { label: string; value: ReactNode; note?: ReactNode; tone?: string }) {
  return (
    <Card className="p-5">
      <p className="text-sm text-ink-500">{label}</p>
      <p className={cn('tabular mt-1.5 text-2xl font-semibold tracking-tight text-navy-900', tone)}>{value}</p>
      <p className="mt-1 text-[13px] text-ink-500">{note}</p>
    </Card>
  );
}

/** This month's wages, what is still due, and advances not yet recovered. */
function PayrollStats() {
  const now = new Date();
  const from = toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  const { data: month } = useFetch(() => payrollService.list({ from, to: toISODate(now), status: 'active', limit: 200 }).then((r) => r.data), []);
  const { data: due } = useFetch(() => payrollService.list({ status: 'active', paymentStatus: 'unpaid', limit: 200 }).then((r) => r.data), []);
  const { data: part } = useFetch(() => payrollService.list({ status: 'active', paymentStatus: 'partial', limit: 200 }).then((r) => r.data), []);
  const { data: adv } = useFetch(() => payrollService.advances(), []);
  const sum = (arr, k) => (arr ?? []).reduce((a, s) => a + (s[k] ?? 0), 0);
  const dueList = [...(due ?? []), ...(part ?? [])];
  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
      <Stat label="Wages this month" value={month ? money(sum(month, 'grossAmount')) : '…'} note={`${month?.length ?? 0} salary slips`} />
      <Stat label="Paid this month" value={month ? money(sum(month, 'paidAmount')) : '…'} tone="text-emerald-700" note="Against this month's slips" />
      <Stat label="Salary due" value={due && part ? money(sum(dueList, 'balanceAmount')) : '…'} tone={sum(dueList, 'balanceAmount') ? 'text-red-600' : undefined} note={`${dueList.length} slips not fully paid`} />
      <Stat label="Advances pending" value={adv ? money(adv.total) : '…'} tone={adv?.total ? 'text-sun-600' : undefined} note="To recover from salary" />
    </div>
  );
}

export default function PayslipList() {
  const navigate = useNavigate();
  const add = <Button leftIcon="plus" onClick={() => navigate('/payroll/new')}>Make salary slip</Button>;
  return (
    <ListPage
      title="Salary & wages"
      path="/payroll"
      tabs={TABS}
      tabKey="paymentStatus"
      exportName="salary-slips"
      stats={<PayrollStats />}
      filters={[
        dateFilter('Period end'),
        staffFilter,
        { key: 'payType', label: 'Pay type', icon: 'wallet', options: Object.entries(PAY_TYPES).map(([value, label]) => ({ value, label })) },
        { key: 'status', label: 'Status', icon: 'checkCircle', options: [{ value: 'active', label: 'Active' }, { value: 'cancelled', label: 'Cancelled' }] },
      ]}
      searchPlaceholder="Slip no or name"
      actions={
        <>
          <Button variant="outline" leftIcon="moneyOut" onClick={() => navigate('/staff-advances')}>Advances</Button>
          {add}
        </>
      }
      rowTo={(s) => `/payroll/${s._id}`}
      rowActions={(s) =>
        [
          { label: 'View', icon: 'eye', onClick: () => navigate(`/payroll/${s._id}`) },
          s.status === 'active' && s.balanceAmount > 0 && { label: 'Pay salary', icon: 'rupee', onClick: () => navigate(`/transactions/new?payslipId=${s._id}`) },
          { label: 'Attendance', icon: 'calendar', onClick: () => navigate(`/attendance?view=sheet&month=${toISODate(new Date(s.periodTo)).slice(0, 7)}`) },
        ].filter(Boolean)
      }
      columns={[
        { header: 'Slip no.', sort: 'slipNo', sortDesc: true, className: 'whitespace-nowrap', render: (s) => s.slipNo, csv: (s) => s.slipNo },
        { header: 'Staff', sort: 'staff.name', render: (s) => <Cell title={s.staff?.name} sub={labelOf(PAY_TYPES, s.payType)} />, csv: (s) => s.staff?.name },
        { header: 'Period', sort: 'periodTo', sortDesc: true, className: 'whitespace-nowrap', render: (s) => periodText(s), csv: (s) => periodText(s) },
        { header: 'Days paid', sort: 'payableDays', sortDesc: true, align: 'right', render: (s) => s.payableDays, csv: (s) => s.payableDays },
        { header: 'Earnings', sort: 'grossAmount', sortDesc: true, align: 'right', render: (s) => money(s.grossAmount), csv: (s) => s.grossAmount / 100 },
        { header: 'Advance cut', align: 'right', render: (s) => (s.advanceDeduction ? money(s.advanceDeduction) : '-'), csv: (s) => s.advanceDeduction / 100, defaultHidden: true },
        { header: 'Net pay', sort: 'netAmount', sortDesc: true, align: 'right', render: (s) => <span className="font-semibold text-navy-900">{money(s.netAmount)}</span>, csv: (s) => s.netAmount / 100 },
        { header: 'Due', sort: 'balanceAmount', sortDesc: true, align: 'right', render: (s) => (s.balanceAmount > 0 ? <span className="font-medium text-red-600">{money(s.balanceAmount)}</span> : <span className="text-ink-300">-</span>), csv: (s) => s.balanceAmount / 100 },
        {
          header: 'Payment',
          sort: 'paymentStatus',
          render: (s) => (s.status === 'cancelled' ? <Badge tone="red" dot>Cancelled</Badge> : <StatusBadge map={PAYMENT_STATUS} value={s.paymentStatus} />),
          csv: (s) => (s.status === 'cancelled' ? 'Cancelled' : labelOf(PAYMENT_STATUS, s.paymentStatus)),
        },
      ]}
      mobile={(s) => ({
        title: s.staff?.name,
        subtitle: `${s.slipNo} · ${periodText(s)}`,
        right: money(s.netAmount),
        badge: s.status === 'cancelled' ? <Badge tone="red">Cancelled</Badge> : <StatusBadge map={PAYMENT_STATUS} value={s.paymentStatus} />,
      })}
      empty={{ icon: 'wallet', title: 'No salary slips yet', message: 'Mark attendance, then make a salary slip for each person.', action: add }}
    />
  );
}
